import { Component, Input, OnInit } from '@angular/core';
import {
  ComparableCollectionResponse,
  ComparableEvidenceRow,
} from 'src/app/models/comparableEvidence';
import { AdminService } from 'src/app/services/admin-service';

/**
 * Comparable EVIDENCE collection for a registration — the permanent home of
 * the local comparable-collection-lab. Collects and labels sold + market
 * comparables for manual review; deliberately performs NO valuation (no
 * range, weighting, recency, index, confidence or blending).
 *
 * Used two ways (same endpoint, process=admin-comparables):
 *   - as the "Comparables" tab of /account/admin
 *   - embedded in the blog editor ([embedded]="true") so evidence can be
 *     pulled while writing a post
 */
@Component({
  selector: 'pla-admin-comparables',
  templateUrl: './admin-comparables.component.html',
  styleUrls: ['./admin-comparables.component.scss'],
})
export class AdminComparablesComponent implements OnInit {
  /** Compact mode for the blog-editor embed: tighter heading, audit collapsed. */
  @Input() public embedded: boolean = false;
  /** Optional pre-filled plate (e.g. from the blog post being written). */
  @Input() public initialPlate: string = '';

  public plate: string = '';
  public term: string = '';
  public limit: number = 200;
  public minScore: number = 70;
  public band: number = 5;

  public loading: boolean = false;
  public error: string = null;
  public result: ComparableCollectionResponse = null;

  constructor(private adminService: AdminService) {}

  ngOnInit(): void {
    if (this.initialPlate) {
      this.plate = this.initialPlate;
    }
  }

  public run(): void {
    const plate = (this.plate || '').trim();
    if (!plate) return;
    this.loading = true;
    this.error = null;
    this.result = null;
    this.adminService
      .GetComparables(plate, (this.term || '').trim(), this.limit, this.minScore, this.band)
      .subscribe(
        (res: ComparableCollectionResponse) => {
          this.loading = false;
          if (!res || (res as any).plaError) {
            this.error = (res && (res as any).plaError) || 'No response';
            return;
          }
          this.result = res;
        },
        () => {
          this.loading = false;
          this.error = 'Request failed — are you signed in as an admin?';
        }
      );
  }

  public gbp(pence: number | null): string {
    if (!pence || pence <= 0) return '—';
    return '£' + Math.round(pence / 100).toLocaleString('en-GB');
  }

  public score(s: number | null): string {
    return s === null || s === undefined ? '—' : s.toFixed(1);
  }

  /** Cheapest dealer name with a "+N more" hint when others also list it. */
  public src(row: ComparableEvidenceRow): string {
    let s = row.source || '—';
    if (row.dealers && row.dealers.length > 1) {
      s += ` (+${row.dealers.length - 1} more)`;
    }
    return s;
  }

  public lane(row: ComparableEvidenceRow): string {
    if (row.lane === 'sold') {
      return row.bucket === 'genuine_sold' ? 'sold' : 'previous';
    }
    return 'market';
  }

  public sectionCount(section: { total: number; rows: any[] }): string {
    if (!section) return '0';
    return (
      `${section.total}` +
      (section.total > section.rows.length ? ` · newest ${section.rows.length}` : '')
    );
  }

  public get hasTerm(): boolean {
    return !!(this.result && this.result.term);
  }

  public get queryErrors(): number {
    if (!this.result) return 0;
    return this.result.query_log.filter((q) => !!q.error).length;
  }
}
